import { format, fromUnixTime } from "date-fns";
import { useSelector } from "react-redux";
import Avatar from "../../../components/Avatar";
import { Card } from "../../../components/Card";
import { Carousel } from "../../../components/Carousel";
import { CardDivider } from "../../../components/Divider";
import { Flex } from "../../../components/Flex";
import { H1, H3 } from "../../../components/Heading";
import { InstagramIcon } from "../../../components/icons/InstagramIcon";
import { LinkIcon } from "../../../components/icons/LinkIcon";
import { PicturesIcon } from "../../../components/icons/PictureIcon";
import { TikTokIcon } from "../../../components/icons/TikTokIcon";
import { VideoIcon } from "../../../components/icons/VideoIcon";
import { StyledMarkdown } from "../../../components/StyledMarkdown";
import { Text } from "../../../components/Text";
import { View } from "../../../components/View";
import {
  BrandContentBriefQuery,
  MediaType,
  useGetBrandQuery,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import styled, { ThemeProp } from "../../../styles";
import { formatFriendlyMediaName } from "../../../utils/content";

interface PreviewBriefProps {
  image?: string;
  title: string;
  dueDate?: number;
  description: string;
  deliverables: BrandContentBriefQuery["contentBrief"]["deliverables"];
  budget: number;
}

export const PreviewBrief = ({
  image,
  title,
  dueDate,
  description,
  deliverables,
  budget,
}: PreviewBriefProps) => {
  const activeBrand = useSelector(authSelectors.activeBrand);

  return (
    <View margin="0 0 l" style={{ height: "100%" }}>
      {/* Title */}
      <div style={{ position: "relative", zIndex: -1 }}>
        <Carousel
          items={
            image
              ? [
                  {
                    url: image,
                    type: MediaType.Image,
                  },
                ]
              : []
          }
        />
      </div>
      <Card
        style={{
          marginTop: -12,
          zIndex: 1,
          overflow: "visible",
          position: "relative",
          paddingTop: 38,
          height: "100%",
        }}
      >
        <AvatarWrap>
          <Avatar url={activeBrand?.logo} size={68} borderSize={2} />
        </AvatarWrap>
        <H1 margin="l 0 0" style={{ textAlign: "center" }}>
          {title}
        </H1>
        <Text margin="s 0 xs" colorPreset="secondary" align="center">
          {activeBrand?.name}
        </Text>
        {/* Content Type and Delivery Info */}
        <Text
          margin="xxs 0 m"
          style={{ textAlign: "center" }}
          size="s"
          weight="semi"
        >
          UGC Only
          {budget > 0 && ` · £${budget} Budget`}
          {dueDate &&
            ` · Delivery by ${format(fromUnixTime(dueDate), "do MMMM")}`}
        </Text>

        <CardDivider margin="xl 0 xl 0" />

        {/* Details Section */}
        <View margin="l l 0">
          <H3 margin="0 0 0">Details</H3>
          <StyledMarkdown markdown={description} />

          <AboutBrand />
        </View>

        <CardDivider margin="xl 0" />

        {/* Deliverables Section */}
        <View margin="0 l xl">
          <H3 margin="0 0 s">Deliverables</H3>
          {deliverables.length > 0 &&
            deliverables.map((deliverable) => (
              <DeliverablePreview
                key={deliverable.id}
                deliverable={deliverable}
              />
            ))}
        </View>
      </Card>
    </View>
  );
};

const DeliverablePreviewWrap = styled.div`
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  margin-bottom: ${(p) => p.theme.spacing.m};
  background-color: ${(p) => p.theme.color.card.callout};
  width: 100%;
  position: relative;
  box-sizing: border-box;
`;

const DeliverablePreview = ({
  deliverable,
}: {
  deliverable: BrandContentBriefQuery["contentBrief"]["deliverables"][0];
}) => {
  return (
    <DeliverablePreviewWrap>
      <View padding="l">
        <Flex align="flex-start" direction="row">
          <View>
            {deliverable.mediaType === MediaType.Video ? (
              <VideoIcon width={26} colorPreset="secondary" />
            ) : (
              <PicturesIcon width={26} colorPreset="secondary" />
            )}
          </View>
          <Flex direction="column" margin="0 0 0 xs" style={{ flex: 1 }}>
            <Flex align="center" direction="row" justify="space-between">
              <Text size="m" margin="0 0 0 s" weight="semi">
                {formatFriendlyMediaName(
                  deliverable.aspectRatio,
                  deliverable.mediaType
                )}
              </Text>
              <Text size="l" margin="0 0 0 s" weight="bold" colorPreset="link">
                x{deliverable.quantity}
              </Text>
            </Flex>
            <Text size="s" margin="xxs 0 0 s" colorPreset="secondary">
              {deliverable.description}
            </Text>
          </Flex>
        </Flex>
      </View>
      <ReferenceWrap>
        {deliverable.referenceLinks &&
          deliverable.referenceLinks.length > 0 &&
          deliverable.referenceLinks.map((reference, index) => (
            <Reference key={index} href={reference} target="_blank">
              <Flex align="center" direction="row" justify="space-between">
                <Text size="xs" margin="0 s 0 0" colorPreset="text">
                  Reference {index + 1}
                </Text>
                <LinkIcon width={14} height={14} colorPreset="text" />
              </Flex>
            </Reference>
          ))}
      </ReferenceWrap>
    </DeliverablePreviewWrap>
  );
};

const AboutBrand = () => {
  const client = useGqlClient();
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const { data: brand } = useGetBrandQuery(client, {
    id: activeBrandId ? activeBrandId : "",
  });

  if (!brand) {
    return null;
  }

  return (
    <AboutBrandWrap>
      <Flex align="center" direction="row" justify="space-between">
        <Text margin="0 0 0" weight="bold" size="l">
          About {brand?.brand?.name}
        </Text>
      </Flex>
      <Text margin="0 0 m" colorPreset="secondary" size="s">
        {brand.brand?.longDescription || "No description provided"}
      </Text>
      <BrandSocialsGrid>
        {brand.brand?.instagramHandle && (
          <a
            href={`https://www.instagram.com/${brand.brand.instagramHandle}`}
            target="_blank"
            rel="noreferrer"
          >
            <InstagramIcon width={24} height={24} />
          </a>
        )}
        {brand.brand?.tikTokHandle && (
          <a
            href={`https://www.tiktok.com/@${brand.brand.tikTokHandle}`}
            target="_blank"
            rel="noreferrer"
          >
            <TikTokIcon width={24} height={24} />
          </a>
        )}
      </BrandSocialsGrid>
    </AboutBrandWrap>
  );
};

const AboutBrandWrap = styled.div`
  padding: ${(p) => p.theme.spacing.l};
  border-radius: ${(p) => p.theme.misc.borderRadiusLarge};
  margin-top: ${(p) => p.theme.spacing.l};
  border: 1px solid ${(p) => p.theme.color.card.divider};
`;

const BrandSocialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 24px);
  column-gap: ${(p) => p.theme.spacing.m};
  height: 24px;
`;

const AvatarWrap = styled.div`
  position: absolute;
  top: -36px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const Reference = styled.a`
  padding: 6px ${(p: ThemeProp) => p.theme.spacing.m} 5px;
  background-color: ${(p: ThemeProp) => p.theme.color.card.background};
  border-radius: ${(p: ThemeProp) => p.theme.misc.borderRadiusSmall};
  border: 1px solid ${(p: ThemeProp) => p.theme.color.button.secondaryBorder};
  margin-right: ${(p: ThemeProp) => p.theme.spacing.s};
  text-decoration: none;
  flex-shrink: 0;
  white-space: nowrap;
`;

const ReferenceWrap = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  flex-wrap: nowrap;
  margin-top: ${(p) => p.theme.spacing.s};
  padding: 0 ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.l};
`;
